<template>
  <div class="flex flex-col">
    <div class="inline-flex items-center p-3 gap-1">
      <div class="flex px-2 cursor-pointer" @click="$emit('close')">
        <UIcon name="i-ph-arrow-left" class="h-5 w-5"/>
      </div>
      <!-- <UDivider class="h-8 w-2" orientation="vertical"/> -->
      <div class="w-full relative">
        <UInput padded v-model="searchText" size="xl" placeholder="Start typing" :ui="{ color: { white: { outline: 'focus:ring-0.5 focus:ring-gray-400 focus:bg-transparent' } } }" @focus="onFocus" 
          @keyup.enter="handleSearch"/>
        <div class="absolute top-2 right-14 cursor-pointer text-gray-400" v-if="searchText.length" @click="clearSearch" >
          <UIcon name="i-heroicons-x-mark" class="h-7 w-6"/>
        </div>
        <div class="absolute top-0 right-0 h-11 w-11 flex items-center justify-center bg-orange-200 rounded-none" @click="handleSearch">
          <UIcon name="i-heroicons-magnifying-glass" class="h-6 w-7"/>
        </div>
      </div>
    </div>
    <UDivider class="h-1 pb-5"/>
    <div class="flex flex-col gap-2 h-svh md:h-96 overflow-auto">
      <div class="flex flex-col gap-4" v-if="!isValidSearchText || emptyState">
        <div class="px-5 py-2" v-if="emptyState">
          <div class="flex flex-col items-center w-full gap-1">
            <UIcon name="i-ph-warning-circle" class="h-12 w-12"/>
            <p class="text-md text-sky-700 font-bold">We couldn't find any matching results.</p>
            <p class="text-sm text-gray-500">Try checking your spelling or explore trending searches.</p>
          </div>
        </div>
        <UDivider class="h-1" v-if="emptyState"/>
        <V4RecentSearchComponent class="px-5" @selected="setSearchText"/>
        <UDivider class="h-1"/>
        <V4TrendingSearchComponent class="px-5"/>
        <UDivider class="h-1"/>
      </div>
      <div class="flex flex-col gap-4" v-if="isValidSearchText">
        <div v-if="loading || categories.length > 0">
          <V4SearchGroupListComponent class="px-5 cursor-pointer" title="Explore Categories" :items="categories" :loading="loading" @navigate="navigate"/>
          <UDivider class="h-1 pt-5"/>
        </div>
        <div v-if="loading || providers.length > 0">
          <V4SearchGroupCardComponent class="px-5 cursor-pointer" title="Sellers" :items="providers" :loading="loading" @navigate="navigate"/>
          <UDivider class="h-1 pt-5"/>
        </div>
        <div v-if="loading || products.length > 0">
          <V4SearchGroupCardComponent class="px-5 cursor-pointer" title="Products" :items="products" :loading="loading" @navigate="navigate"/>
        </div>
      </div>
      <div><p class="h-12"></p></div>
    </div>
  </div>
</template>

<script setup>
import { GoogleAnalytics } from '~/google_analytics/events';
import { EventParameter } from '~/google_analytics/events.enum';


const props = defineProps({
  filters: { type: Object, default: {} }
});
const route = useRoute();
const router = useRouter()
const { gtag } = useGtag();
const auth = useAuth();
  const isAuthenticated = auth && auth.access_token;
  const userData = isAuthenticated ? {
    user_name: auth.user.first_name,
    user_phone: auth.user.phone,
    user_email: auth.user.email,
  } : {
    user_name: null,
    user_phone: null,
    user_email: null
  };
const { latitude, longitude } = useLocation().gps;
const searchText = ref('');
const categories = ref([]);
const providers = ref([]);
const products = ref([]);
const analyticsEvents = useAnalyticsEvents();
const loading = ref(false);
const emptyState = ref(false);
const location = useLocation();
const { addressText } = useAddressText(location);
const resultCount = computed(() => [...categories.value, ...providers.value, ...products.value].length);
const isValidSearchText = computed(() => searchText.value?.length >= 3);
let hasFocused = false;
let hasTrackedInitiated = false;
let lastSearchDurationMs = undefined;

const trackSearchPerformed = () => {
  if (!isValidSearchText.value) return;
  const totalResults = resultCount.value;

  useAnalytics(analyticsEvents.SEARCH_PERFORMED, {
    search_term: searchText.value.trim(),
    num_results: totalResults,
    search_response_time_ms: lastSearchDurationMs,
    result_status: totalResults > 0 ? 'results_found' : 'no_results',
    scope: 'search_modal',
  });
};

const debouncedSearch = useDebounce(async () => {
  if (!isValidSearchText.value) return;

  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
  loading.value = true;
  emptyState.value = false;
  categories.value = [];
  providers.value = [];
  products.value = [];

  try {
    const [categoriesData, providersData, productsData] = await Promise.all([
      fetchCategories(),
      fetchProviders(),
      fetchProducts()
    ]);

    categories.value = categoriesData;
    providers.value = providersData;
    products.value = productsData
  } finally {
    loading.value = false;
    emptyState.value = resultCount.value === 0;
    const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    lastSearchDurationMs = Math.max(0, Math.round(endTime - startTime));
    trackSearchPerformed();
  }
}, 500);

watch(searchText, (value) => {
  if (!hasTrackedInitiated && value?.trim()) {
    hasTrackedInitiated = true;
    useAnalytics(analyticsEvents.SEARCH_INITIATED, {
      search_term: value.trim(),
    });
  }
  debouncedSearch();
});

const fetchCategories = async () => {
  const { data } = await useAPIHandler('get', '/suggestions', {
    query: {
      text: searchText.value.trim(),
      c: 1, s: 0, p: 0, limit: 20
    }
  });

  return data.categories.slice(0, 3).map(({ name, parent, search_tag }) => ({
    label: name,
    suffix: `in ${parent}`,
    icon: 'i-ph-arrow-up-right',
    href: `/categories/search-v2?parent=${encodeURIComponent(search_tag)}&q=${encodeURIComponent(name)}`
  }));
}

const fetchProviders = async () => {
  const { data, error } = await useAPIHandler('post', '/v2/discover', {
    body: {
      location: { gps: [latitude, longitude] },
      ...(props.filters.categories ? { categories: props.filters.categories } : {}),
      ...(props.filters.providers ? { providers: props.filters.providers } : {}),
      ...(props.filters.max_distance ? { max_distance: props.filters.max_distance } : {}),
      search_text: searchText.value.toLowerCase(),
      fields: ['ukid', 'name', 'images', 'categories', 'locations', 'distance', 'is_open_now', 'is_deliverable'],
      sort: [{ field: 'distance', order: 'asc' }],
      page_size: 20,
      partner_id: useRuntimeConfig().public.partnerCode
    }
  });
  if (error) return [];

  const getPriority = ({ is_open_now, is_deliverable }) =>
    is_open_now && is_deliverable ? 0 : is_deliverable ? 1 : 2;

  const formatDistance = (distance) =>
    distance <= 1000
      ? `${Math.ceil(distance / 10) * 10} m`
      : `${(distance / 1000).toFixed(1)} km`;

  return data.providers
    .sort((a, b) => getPriority(a) - getPriority(b))
    .map(({ name, distance, categories, is_open_now, is_deliverable, images, locations, ukid }) => {
      const uniqCategories = [...new Set(categories.filter(item => item !== null))];
      const messages = [
        ...(uniqCategories.length ? [uniqCategories[0]] : []),
        formatDistance(distance),
        ...(uniqCategories.length && uniqCategories[0].toUpperCase() === 'F&B' ? [locations.address.locality] : [locations.address.city])
      ];
      const tags = [
        ...(!is_open_now ? [`<span class="text-red-500">Closed</span>`] : []),
        ...(is_open_now && !is_deliverable ? [`<span class="text-red-500">Non-deliverable</span>`] : [])
      ]

      return {
        label: name,
        avatar: images.thumbnail,
        message: messages.join(' | '),
        tags,
        href: `/providers/${ukid}?loc_id=${locations.id}`
      };
    });
};

const fetchProducts = async () => {
  const { path, query } = route;

  const page = path.includes('/search-v2')
  ? 'subcategory'
  : path.includes('/categories')
  ? 'category'
  : 'home';

  const categorySlugToName = {
  fandb: "F&B",
  handk: "Home & Kitchen",
  handw: "Health & Wellness"
  };

  const rawCategory = query.parent || null;
  const category = rawCategory ? categorySlugToName[rawCategory] || rawCategory : null;

  const subcategory = page === 'subcategory' ? query.q || null : null;

  const cookie = {
    auth: encodeURIComponent(JSON.stringify({
      user: {
        first_name: auth?.user?.first_name || null,
        last_name: auth?.user?.last_name || null,
        phone: auth?.user?.phone || null,
        email: auth?.user?.email || null,
        dob: auth?.user?.dob || null,
        gender: auth?.user?.gender || null,
        partner: auth?.user?.partner || null
      }
    })),
    location: encodeURIComponent(JSON.stringify({
      area_code: location.area_code || null,
      city: location.city || null,
      state: location.state || null,
      country: location.country || null,
      gps: {
        lat: latitude,
        lng: longitude
      }
    }))
  };

  const { data, error } = await useAPIHandler('post', '/v2/search', {
    body: {
      gps: {
        lat: latitude,
        lng: longitude
      },
      ...(category ? { categories: [category] } : {}),
      ...(subcategory ? { sub_categories: [subcategory] } : {}),
      text: searchText.value.toLowerCase(),
      limit: 20,
      partner_id: useRuntimeConfig().public.partnerCode
    }
  });

  if (error) return [];

  return data.map(({ name, category, images, stock, provider, sku_id, time_to_ship }) => {
    const isOutOfStock = stock ? Number(stock.available) === 0 : false;
    // const isFandB = category.parent.name.toUpperCase() === 'F&B';
    // const tts = useDayjs().duration(time_to_ship).days();
    // const ttsText = tts <= 1 ? 'Ships in same day' : `Ships in ${tts} days`;

    const messages = [
      category.name,
      ...(isOutOfStock ? [`<span class="text-red-500">Out of stock</span>`] : [provider.name])
    ];
    return {
      label: name,
      avatar: images.thumbnail,
      message: messages.join(' | '),
      href: `/products/${sku_id}`
    };
  });
};

const updateSearchHistory = (value) => {
  const searchesRaw = localStorage.getItem('searches');
  const searches = searchesRaw ? JSON.parse(searchesRaw) : [];
    
  const normalizedText = value.toLowerCase();
  const uniqueSearchesMap = new Map(
    searches.map((item) => [item.name.toLowerCase(), item])
  );
  uniqueSearchesMap.delete(normalizedText);
  const uniqueSearches = Array.from(uniqueSearchesMap.values());
  if (uniqueSearches.length === 5) {
    uniqueSearches.pop();
  }
  uniqueSearches.unshift({ name: value });
  localStorage.setItem('searches', JSON.stringify(uniqueSearches));
}

const setSearchText = async (value) => {
  const typedTerm = searchText.value;
  searchText.value = value;
  updateSearchHistory(value);
  useAnalytics(analyticsEvents.SEARCH_SUGGESTION_CLICKED, {
    search_term: typedTerm,
    clicked_suggestion: value,
  });
}
const navigate = (url) => {
  const newURL = new URL(url, window.location.origin);
  const isCategoriesRoute = route.path.includes('/categories');
  const query = Object.fromEntries(newURL.searchParams.entries());
  const pathParts = newURL.pathname.split('/').filter(Boolean);
  const section = pathParts[0]; // "providers" or "products"
  const rawItemId = pathParts[1];
  const cleanItemId = rawItemId.split("?")[0]; // ✅ remove query params

  let clickedName = null;

  if (section === 'products') {
    const foundProduct = products.value.find(p => {
      const productIdFromHref = p.href.split("/products/")[1];
      return productIdFromHref === cleanItemId;
    });
    clickedName = foundProduct ? foundProduct.label : null;
  } 
  else if (section === 'providers') {
    const foundProvider = providers.value.find(p => {
      const providerIdFromHref = p.href.split("/providers/")[1].split("?")[0];
      return providerIdFromHref === cleanItemId;
    });
    clickedName = foundProvider ? foundProvider.label : null;
  } else if (section === 'categories') {
    const foundCategory = categories.value.find(c => c.href === url);
    clickedName = foundCategory ? foundCategory.label : null;
  }

  const targetPath = newURL.pathname + (newURL.search ? newURL.search : '');
  const currentFullPath = route.fullPath;

  // Normalize paths for comparison
  const normalizePath = (path) => decodeURIComponent(path.replace(/\+/g, ' '));

  if (normalizePath(targetPath) === normalizePath(currentFullPath)) {
    window.location.href = targetPath;
    return;
  }

  console.log('clickedName:', clickedName);

  let resultPosition;

  if (section === 'products') {
    const productIndex = products.value.findIndex((p) => {
      const productIdFromHref = p.href.split("/products/")[1];
      return productIdFromHref === cleanItemId;
    });
    if (productIndex !== -1) {
      resultPosition = productIndex + 1;
    }
  } else if (section === 'providers') {
    const providerIndex = providers.value.findIndex((p) => {
      const providerIdFromHref = p.href.split("/providers/")[1].split("?")[0];
      return providerIdFromHref === cleanItemId;
    });
    if (providerIndex !== -1) {
      resultPosition = providerIndex + 1;
    }
  } else if (section === 'categories') {
    const categoryIndex = categories.value.findIndex((c) => c.href === url);
    if (categoryIndex !== -1) {
      resultPosition = categoryIndex + 1;
    }
  }

  if (section === 'providers' || section === 'products' || section === 'categories') {
    GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: 'search suggestion clicked',
      [EventParameter.SEARCH_QUERY]: searchText.value,
      [EventParameter.CLICKED_SUGGESTION]: clickedName,
      [EventParameter.PRODUCT_SKU]: section === 'products' ? cleanItemId : null,
      [EventParameter.PROVIDER_UKID]: section === 'providers' ? cleanItemId : null,
      [EventParameter.RESULT_POSITION]: typeof resultPosition === 'number' ? resultPosition : undefined,
    });
  }
  if (section === 'providers') {
    logCtaEvent({ type: 'provider', data: cleanItemId, isCategoriesRoute });
  } else if (section === 'products') {
    logCtaEvent({ type: 'product', data: cleanItemId, isCategoriesRoute });
  }
  if (section === 'providers' || section === 'products' || section === 'categories') {
    useAnalytics(analyticsEvents.SEARCH_SUGGESTION_CLICKED, {
      search_term: searchText.value,
      clicked_suggestion: clickedName || cleanItemId,
    });
  }
  if (section === 'providers' || section === 'products' || section === 'categories') {
    useAnalytics(analyticsEvents.SEARCH_SUGGESTION_CLICKED, {
      search_term: searchText.value,
      clicked_result: clickedName || cleanItemId,
      result_position: typeof resultPosition === 'number' ? resultPosition : undefined,
    });
  }
  navigateTo({ path: url, query });
  updateSearchHistory(searchText.value);
}

const onFocus = () => {
  if (hasFocused) return;
  hasFocused = true;
  if (!hasTrackedInitiated) {
    hasTrackedInitiated = true;
    useAnalytics(analyticsEvents.SEARCH_INITIATED, {
      search_term: searchText.value?.trim() || undefined,
    });
  }

};
const logCtaEvent = ({ type, data, isCategoriesRoute }) => {
  const baseEvent = {
    search_query: searchText.value,
    action: 'search',
    referrer: isCategoriesRoute ? 'Category Screen' : 'Home Screen',
    user_address: addressText.value,
    ...userData,
  };

  if (type === 'provider') {
    GoogleAnalytics.cta({
      ...baseEvent,
      [EventParameter.CTA_NAME]: 'store click',
      [EventParameter.PROVIDER_UKID]: data,
    });
  } else if (type === 'product') {
    GoogleAnalytics.cta({
      ...baseEvent,
      [EventParameter.CTA_NAME]: 'product click',
      [EventParameter.PRODUCT_SKU]: data,
    });
  }
};

const clearSearch = () => {
  GoogleAnalytics.cta({
    [EventParameter.CTA_NAME]: 'x button',
    [EventParameter.SEARCH_QUERY]: searchText?.value,
  });
  searchText.value = ''
}

const handleSearch = () => {
  updateSearchHistory(searchText.value);
  if (searchText.value && isValidSearchText.value && !emptyState.value) {
    const parentCategory = route.path.includes('/categories')
      ? route.query.parent
      : null;

    let searchUrl = parentCategory
      ? `/search?c=${encodeURIComponent(parentCategory)}&q=${encodeURIComponent(searchText.value)}`
      : `/search?q=${encodeURIComponent(searchText.value)}`;

    router.push(searchUrl);
  }
}
</script>
